import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Paperclip, RefreshCw, X } from "lucide-react";

import { BASE } from "./shared";

const MAX_BYTES = 10 * 1024 * 1024;

export function PdfAttachmentField({ value, onChange, pw, testIdPrefix = "manual-email" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const remove = async () => {
    if (value?.id) {
      try { await axios.delete(`${BASE}/email-attachments/${value.id}`, { headers: { "X-Admin-Password": pw } }); }
      catch (_) {}
    }
    onChange(null);
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") || (file.type && file.type !== "application/pdf")) {
      toast.error("Choose a PDF document."); return;
    }
    if (file.size > MAX_BYTES) { toast.error("PDF attachments must be 10 MB or smaller."); return; }
    setUploading(true);
    try {
      if (value?.id) await remove();
      const form = new FormData();
      form.append("file", file);
      const response = await axios.post(`${BASE}/email-attachments`, form, {
        headers: { "X-Admin-Password": pw },
      });
      onChange(response.data);
      toast.success("PDF attached.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "PDF upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-attachment-field`} className="rounded-lg border border-[#D6DCE3] bg-[#FAFAF7] p-3">
      <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`${testIdPrefix}-attachment-input`} onChange={(event) => upload(event.target.files?.[0])}/>
      {value ? (
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 shrink-0 text-[#0D2F4D]"/>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#0D2F4D]" data-testid={`${testIdPrefix}-attachment-name`}>{value.filename}</p>
            <p className="text-xs text-[#6F8FA8]">{(value.size / 1024 / 1024).toFixed(2)} MB · PDF</p>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()} data-testid={`${testIdPrefix}-attachment-replace`} className="p-1.5 text-[#6F8FA8] hover:text-[#0D2F4D]" title="Replace PDF"><RefreshCw className="h-4 w-4"/></button>
          <button type="button" onClick={remove} data-testid={`${testIdPrefix}-attachment-remove`} className="p-1.5 text-red-500 hover:text-red-700" title="Remove PDF"><X className="h-4 w-4"/></button>
        </div>
      ) : (
        <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`${testIdPrefix}-attachment-add`} className="inline-flex items-center gap-2 text-sm font-bold text-[#0D2F4D] disabled:opacity-50">
          <Paperclip className="h-4 w-4"/>{uploading ? "Uploading PDF…" : "Attach PDF"}
        </button>
      )}
      <p className="mt-2 text-[10px] text-[#6F8FA8]">PDF only · maximum 10 MB · attached to this email only</p>
    </div>
  );
}